/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : InputField.tsx
// 설  명 : MUI TextField를 감싸는 공용 입력 컴포넌트 - size/너비 등 프로젝트 기본값을 이 파일 한 곳에서
//         관리하기 위해 만듦(2026-09-27). 기존 <TextField .../> 사용법과 100% 호환되는 드롭인 대체라
//         props를 그대로 받아 TextField에 넘기고, 화면에서 명시적으로 준 prop(size/sx/width 등)은
//         항상 아래 기본값을 덮어씀.
//   - 기본 size : 'small' (조건부/폼 영역 표준 행 높이 - 관리번호/계약명 등 gform 행과 동일한 높이)
//   - 기본 width : 160px (명시적으로 width/minWidth/fullWidth를 준 경우는 그 값이 우선함)
//   - readOnly prop(신규) : 조회전용 필드용. 기존 disabled(완전 비활성 - 선택/복사 불가)와 달리
//     MUI의 진짜 HTML readonly(InputProps.readOnly)를 써서 텍스트 선택/복사는 되게 하면서, 기존에
//     disabled로 보이던 회색 배경 느낌은 sx로 그대로 유지함.
//   - 아직 안 하는 것 : Select(콤보)/DatePicker 전용 처리는 별도 고민 후 결정(2026-09-27 보류).
//   - select 콤보 로딩중 방어(2026-09-27 추가) : useComboBox 등으로 옵션을 비동기로 받아오는 화면에서,
//     실제 값(예: item_grp1='L')은 즉시 세팅되는데 옵션 목록은 아직 도착 전이라 값과 일치하는 MenuItem이
//     없어서 MUI가 "out-of-range value" 경고를 매번 찍는 문제가 있었음. select이고 현재 value가 지금
//     rendering된 MenuItem들 중에 없으면, 안 보이는 MenuItem을 하나 끼워넣어 경고를 막음(실제 옵션이
//     도착하면 그 안에 값이 포함되므로 이 조건이 꺼져서 자동으로 사라짐 - 중복 옵션 없음).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import type { TextFieldProps } from '@mui/material/TextField';

// TextFieldProps는 variant(outlined/filled/standard)별 유니온 타입이라 interface extends가 안 됨(TS2312) - 교차(&)로 대체.
export type InputFieldProps = TextFieldProps & {
  /** 조회전용 표시(선택/복사는 되지만 수정은 안 됨) - 기존 disabled와는 다름(disabled는 완전 비활성). */
  readOnly?: boolean;
};

// 기존에 disabled TextField가 쓰던 회색 배경 느낌을 readOnly(진짜 HTML readonly)에도 그대로 유지하기 위한 스타일.
const READONLY_SX = {
  '& .MuiInputBase-input': { cursor: 'default' },
  '& .MuiOutlinedInput-root, & .MuiFilledInput-root, & .MuiInput-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
} as const;

const InputField = React.forwardRef<HTMLDivElement, InputFieldProps>(function InputField(
  { readOnly, size, sx, fullWidth, InputProps, select, value, children, ...rest },
  ref
) {
  // fullWidth를 쓰거나 sx에서 이미 width/minWidth를 지정했으면 기본 width(160px)를 강제하지 않음.
  const callerSx = (sx as any) || {};
  const hasExplicitWidth = fullWidth || 'width' in callerSx || 'minWidth' in callerSx;
  const mergedSx = [
    hasExplicitWidth ? {} : { width: 160 },
    readOnly ? READONLY_SX : {},
    sx,
  ];

  // select 콤보 로딩중 방어(위 주석 참고) - 현재 value가 지금 rendering된 MenuItem 중에 없으면 안 보이는
  // MenuItem을 하나 끼워넣음.
  let effectiveChildren = children;
  if (select && value !== undefined && value !== null && value !== '') {
    const kids = children ? React.Children.toArray(children) : [];
    const values = kids.map((c: any) => c?.props?.value);
    if (!values.includes(value)) {
      effectiveChildren = [
        <MenuItem key={`__pending_${String(value)}`} value={value as any} style={{ display: 'none' }} />,
        ...kids,
      ];
    }
  }

  return (
    <TextField
      ref={ref}
      size={size ?? 'small'}
      fullWidth={fullWidth}
      sx={mergedSx as any}
      InputProps={readOnly ? { ...InputProps, readOnly: true } : InputProps}
      select={select}
      value={value}
      {...rest}
    >
      {effectiveChildren}
    </TextField>
  );
});

export default InputField;
